import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { ClassroomTeam, PortfolioSessionContext, buildPlanSessionId } from '../../classroom/types';
import { ClassroomPortfolioService } from '../../classroom/ClassroomPortfolioService';
import { ResolvedSessionPlan, formatSlotDate } from '../../utils/sessionPlan';
import ClassroomModal from './ClassroomModal';
import CameraCapture from './CameraCapture';

interface UploadPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  squadId: string;
  teams: ClassroomTeam[];
  plan: ResolvedSessionPlan;
  initialTeamId?: string | null;
  initialSlotIndex?: number | null;
  liveSessionId?: string | null;
  classStatus?: 'next' | 'current';
  currentLessonId?: string;
}

type PreviewItem = { id: string; file: File; url: string };

const UploadPhotosModal: React.FC<UploadPhotosModalProps> = ({
  isOpen,
  onClose,
  squadId,
  teams,
  plan,
  initialTeamId,
  initialSlotIndex,
  liveSessionId,
  classStatus,
  currentLessonId,
}) => {
  const { currentUser } = useAuth();
  const activeTeams = useMemo(() => teams.filter((t) => !t.archived), [teams]);

  const defaultSlot = useMemo(() => {
    if (typeof initialSlotIndex === 'number' && plan.slots[initialSlotIndex]) {
      return initialSlotIndex;
    }
    const live = plan.slots.findIndex((s) => s.status === 'live');
    if (live >= 0) return live;
    const next = plan.slots.findIndex((s) => s.status === 'next');
    if (next >= 0) return next;
    return 0;
  }, [initialSlotIndex, plan.slots]);

  const [teamId, setTeamId] = useState(initialTeamId || activeTeams[0]?.id || '');
  const [slotIndex, setSlotIndex] = useState(defaultSlot);
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneCount, setDoneCount] = useState(0);
  const [mode, setMode] = useState<'pick' | 'camera'>('pick');

  const libraryInputRef = useRef<HTMLInputElement | null>(null);
  const nativeCameraInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTeamId(initialTeamId || activeTeams[0]?.id || '');
    setSlotIndex(defaultSlot);
    setPreviews((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setError(null);
    setDoneCount(0);
    setMode('pick');
  }, [isOpen, initialTeamId, activeTeams, defaultSlot]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slot = plan.slots[slotIndex];
  const team = activeTeams.find((t) => t.id === teamId);

  const session: PortfolioSessionContext | null = slot
    ? {
        sessionId: buildPlanSessionId({
          levelId: slot.levelId,
          date: slot.date,
          slotIndex: slot.index,
          liveSessionId:
            classStatus === 'current' &&
            currentLessonId === slot.levelId &&
            liveSessionId
              ? liveSessionId
              : null,
        }),
        levelId: slot.levelId,
        levelTitle: slot.levelTitle,
        slotIndex: slot.index,
        date: slot.date,
      }
    : null;

  const addFiles = (incoming: File[]) => {
    const images = incoming.filter((f) => f.type.startsWith('image/') || !f.type);
    if (!images.length) {
      setError('Please choose image files (JPG, PNG, HEIC, etc.).');
      return;
    }
    setError(null);
    setPreviews((prev) => [
      ...prev,
      ...images.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  };

  const removePreview = (id: string) => {
    setPreviews((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleUpload = async () => {
    if (!team || !session || !currentUser || previews.length === 0) return;
    setBusy(true);
    setError(null);
    setDoneCount(0);
    try {
      for (let i = 0; i < previews.length; i += 1) {
        await ClassroomPortfolioService.addPhoto({
          squadId,
          teamId: team.id,
          memberIdsSnapshot: team.memberIds,
          session,
          file: previews[i].file,
          uploadedBy: currentUser.uid,
        });
        setDoneCount(i + 1);
      }
      onClose();
    } catch (e) {
      console.error(e);
      setError('Upload failed. If this keeps happening, deploy Storage rules.');
    } finally {
      setBusy(false);
    }
  };

  const openNativeCamera = () => {
    setMode('pick');
    // Allow mode switch to settle, then open native capture
    window.setTimeout(() => nativeCameraInputRef.current?.click(), 50);
  };

  return (
    <ClassroomModal
      isOpen={isOpen}
      title={mode === 'camera' ? 'Take photo' : 'Add photos'}
      onClose={() => {
        if (mode === 'camera') {
          setMode('pick');
          return;
        }
        onClose();
      }}
      width={mode === 'camera' ? 560 : 480}
    >
      {/* Hidden inputs for cross-device fallbacks */}
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          addFiles(Array.from(e.target.files || []));
          e.target.value = '';
        }}
      />
      <input
        ref={nativeCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          addFiles(Array.from(e.target.files || []));
          e.target.value = '';
          setMode('pick');
        }}
      />

      {mode === 'camera' ? (
        <CameraCapture
          onCapture={(file) => {
            addFiles([file]);
            setMode('pick');
          }}
          onClose={() => setMode('pick')}
          onUseNativeCamera={openNativeCamera}
        />
      ) : (
        <div className="cd-modal-form">
          {activeTeams.length === 0 ? (
            <p className="cd-modal-hint">Create a team on the Board before uploading photos.</p>
          ) : (
            <>
              <label className="cd-field">
                <span className="cd-field-label">Team</span>
                <select
                  className="cd-input"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  disabled={busy}
                >
                  {activeTeams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cd-field">
                <span className="cd-field-label">Class / day</span>
                <select
                  className="cd-input"
                  value={slotIndex}
                  onChange={(e) => setSlotIndex(Number(e.target.value))}
                  disabled={busy}
                >
                  {plan.slots.map((s, i) => (
                    <option key={`${s.index}-${s.levelId}`} value={i}>
                      Class {s.index}: {s.levelTitle}
                      {s.date ? ` · ${formatSlotDate(s.date)}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <div className="cd-field">
                <span className="cd-field-label">Photos</span>
                <div className="cd-photo-source-row">
                  <button
                    type="button"
                    className="cd-btn cd-btn-primary"
                    disabled={busy}
                    onClick={() => setMode('camera')}
                  >
                    📷 Take photo
                  </button>
                  <button
                    type="button"
                    className="cd-btn"
                    disabled={busy}
                    onClick={openNativeCamera}
                  >
                    Device camera
                  </button>
                  <button
                    type="button"
                    className="cd-btn"
                    disabled={busy}
                    onClick={() => libraryInputRef.current?.click()}
                  >
                    Choose from library
                  </button>
                </div>
                <span className="cd-field-help">
                  Live camera works on most phones and laptops. If permission is blocked, use Device camera or Library.
                </span>
              </div>

              {previews.length > 0 && (
                <div className="cd-photo-previews">
                  {previews.map((p) => (
                    <div key={p.id} className="cd-photo-preview">
                      <img src={p.url} alt={p.file.name} />
                      <button
                        type="button"
                        className="cd-photo-preview-remove"
                        onClick={() => removePreview(p.id)}
                        disabled={busy}
                        aria-label="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {previews.length > 0 && (
                <span className="cd-field-help">
                  {previews.length} photo{previews.length === 1 ? '' : 's'} ready
                  {busy ? ` · uploaded ${doneCount}/${previews.length}` : ''}
                </span>
              )}

              {error && <p className="cd-modal-error">{error}</p>}

              <div className="cd-modal-actions">
                <button type="button" className="cd-btn" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="cd-btn cd-btn-primary"
                  onClick={handleUpload}
                  disabled={busy || !teamId || previews.length === 0}
                >
                  {busy ? 'Uploading…' : `Upload${previews.length ? ` (${previews.length})` : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </ClassroomModal>
  );
};

export default UploadPhotosModal;
